import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCoordinatedOverlay } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { Badge } from '#pplx/frontend/aether/components/Badge/index.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { PLUS_MENU_ITEM_SELECTED_EVENT_NAME, plusMenuItemSelectedEvent, } from '#pplx/frontend/apps/ask/events/ask-input-plus-menu/analytics.js';
import { getSkillDisplayDescription } from '#pplx/frontend/apps/ask/skill-cards/skillDisplay.js';
import { resolveLocalizedSkillName } from '#pplx/frontend/apps/ask/skills/getComposerSkillMention.js';
import { isDeepResearchSkillName } from '#pplx/frontend/ask-models/deepResearchSkill.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { noop } from 'lodash-es';
import { memo, useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const SKILLS_DESCRIPTION_MAX_CHARS = 50;
export const SkillMenuItem = memo(function SkillMenuItem({ skill, onSkillClick, showDescriptionPopover = true, }) {
    const intl = useIntl();
    const { isOpen, open, close } = useCoordinatedOverlay();
    const { track } = useInstrumentation();
    const timer = useRef(null);
    const handleMouseEnter = useCallback(() => {
        if (!showDescriptionPopover)
            return;
        if (timer.current)
            clearTimeout(timer.current);
        open();
    }, [open, showDescriptionPopover]);
    const handleMouseLeave = useCallback(() => {
        if (!showDescriptionPopover)
            return;
        timer.current = setTimeout(() => {
            close();
        }, 300);
    }, [close, showDescriptionPopover]);
    const skillDescription = getSkillDisplayDescription(skill);
    const description = skillDescription.length > SKILLS_DESCRIPTION_MAX_CHARS
        ? `${skillDescription.slice(0, SKILLS_DESCRIPTION_MAX_CHARS)}…`
        : skillDescription;
    const isDeepResearchSkill = isDeepResearchSkillName(skill.name);
    const displayName = resolveLocalizedSkillName({ skill, intl }) ?? skill.name;
    // TODO(DR-Computer): replace this draft marker with the final branded skill treatment once it is defined.
    const brandedSkillMarker = useMemo(() => isDeepResearchSkill ? (_jsx(Badge, { variant: "neutral", children: _jsx(FormattedMessage, { defaultMessage: "Perplexity", description: "Badge label marking a Perplexity-created branded skill", id: "7789ba2cb8" }) })) : undefined, [isDeepResearchSkill]);
    const handleSelect = useCallback(() => {
        track(plusMenuItemSelectedEvent({
            name: PLUS_MENU_ITEM_SELECTED_EVENT_NAME,
            data: {
                item: 'skill',
                value: skill.id,
            },
        }));
        onSkillClick(skill);
    }, [onSkillClick, skill, track]);
    const triggerElement = useMemo(() => (_jsx("div", { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: _jsx(DropdownMenu.Item, { subtitle: description, leadingAccessory: isDeepResearchSkill ? _jsx(Icon, { icon: $icon('telescope'), size: "small" }) : undefined, trailingAccessory: brandedSkillMarker, onSelect: handleSelect, children: displayName }) })), [
        brandedSkillMarker,
        description,
        displayName,
        handleMouseEnter,
        handleMouseLeave,
        handleSelect,
        isDeepResearchSkill,
    ]);
    if (!showDescriptionPopover) {
        return triggerElement;
    }
    return (_jsx(Popover, { interaction: "hover", appearance: "dark", triggerElement: triggerElement, side: "right", open: isOpen, onOpenChange: noop, children: _jsxs("div", { className: "flex flex-col gap-1 p-2", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(TextPrimitive, { variant: "tinyBold", color: "white", children: displayName }), brandedSkillMarker, skill.scope === 'organization' && (_jsx(Badge, { variant: "neutral", children: _jsx(FormattedMessage, { defaultMessage: "Org", description: "Badge label indicating the skill is organization-scoped", id: "e23ccb6ee3" }) }))] }), _jsx(TextPrimitive, { variant: "tiny", textWrap: "pretty", className: "text-white/50", children: skillDescription })] }) }));
});
//# sourceMappingURL=SkillMenuItem.js.map